// Bloc "Échauffement" de l'éditeur de séance — variante allégée de SeriesTable.
// Calqué sur l'app mobile (screens/training/Exercice.js) : bloc séparé au-dessus
// des séries de travail, ambiance orange, sans RPE.
//
// Les warmups sont stockés dans une table SQL dédiée (gn_serie_echauffement).
// Ils ne comptent PAS pour les records, la progression et le volume — c'est
// pour ça qu'ils vivent en dehors de `series` côté modèle web.
import React, { useState, useRef, useEffect } from "react";
import useSaisieClavier from "./useSaisieClavier";

const DEC_OPTIONS = [0, 25, 50, 75];

function formatDec(d) {
	return `.${String(d).padStart(2, "0")}`;
}

function RowMenu({ open, onClose, onRemove, canRemove }) {
	const ref = useRef(null);
	useEffect(() => {
		if (!open) return;
		const onClick = (e) => {
			if (ref.current && !ref.current.contains(e.target)) onClose();
		};
		document.addEventListener("mousedown", onClick);
		return () => document.removeEventListener("mousedown", onClick);
	}, [open, onClose]);

	if (!open) return null;
	return (
		<div ref={ref} className="editor-series-row-menu" role="menu">
			<button
				className="editor-series-row-menu-item danger"
				onClick={() => { onRemove?.(); onClose(); }}
				disabled={!canRemove}
			>
				Supprimer
			</button>
		</div>
	);
}

export default function WarmupTable({
	warmups,
	onUpdateReps,
	onUpdatePoids,
	onUpdatePoidsDec,
	onAddWarmup,
	onRemoveWarmup,
}) {
	const [openMenu, setOpenMenu] = useState(null);
	const hasWarmups = (warmups || []).length > 0;
	const { refChamp, surTouche, toutSelectionner } = useSaisieClavier((warmups || []).length, onAddWarmup);

	// Pas de warmups : lien discret pour en ajouter un premier
	if (!hasWarmups) {
		return (
			<button
				type="button"
				className="editor-warmup-add-empty"
				onClick={onAddWarmup}
				title="Ajouter un échauffement"
			>
				<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 4 }}>
					<path d="M12 2c1.5 3 4 5 4 8a4 4 0 01-8 0c0-1 .5-2 1-3 .5 1 1.5 2 2.5 2 1 0 1.5-1 1.5-2 0-2-1-3-1-5zm0 20a7 7 0 007-7c0-3-2-5-4-7 1 3-1 5-3 5s-4-2-3-5c-2 2-4 4-4 7a7 7 0 007 7z" />
				</svg>
				Ajouter un échauffement
			</button>
		);
	}

	return (
		<div className="editor-warmup-table">
			<div className="editor-warmup-header">
				<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 4 }}>
					<path d="M12 2c1.5 3 4 5 4 8a4 4 0 01-8 0c0-1 .5-2 1-3 .5 1 1.5 2 2.5 2 1 0 1.5-1 1.5-2 0-2-1-3-1-5zm0 20a7 7 0 007-7c0-3-2-5-4-7 1 3-1 5-3 5s-4-2-3-5c-2 2-4 4-4 7a7 7 0 007 7z" />
				</svg>
				<span>ÉCHAUFFEMENT</span>
			</div>

			{warmups.map((warmup, warmupIndex) => {
				const dec = warmup.poidsDec || 0;
				return (
					<div
						key={warmup.id != null ? `${warmup.id}-${warmupIndex}` : `w-${warmupIndex}`}
						className="editor-warmup-row"
					>
						<span className="col-num">E{warmupIndex + 1}</span>

						<div className="col-reps editor-series-input-wrap">
							<input
								type="number"
								className="editor-series-input"
								value={warmup.reps ?? 0}
								onChange={(e) => onUpdateReps?.(warmupIndex, e.target.value)}
								ref={refChamp(warmupIndex, "reps")}
								onKeyDown={surTouche(warmupIndex, "reps")}
								onFocus={toutSelectionner}
							/>
							<span className="editor-series-input-unit">rep</span>
						</div>

						<div className="col-charge editor-series-charge">
							<input
								type="number"
								step="1"
								className="editor-series-input editor-series-charge-int"
								value={warmup.poids ?? 0}
								onChange={(e) => onUpdatePoids?.(warmupIndex, e.target.value)}
								ref={refChamp(warmupIndex, "poids")}
								onKeyDown={surTouche(warmupIndex, "poids")}
								onFocus={toutSelectionner}
							/>
							<select
								className="editor-series-charge-dec"
								value={dec}
								onChange={(e) => onUpdatePoidsDec?.(warmupIndex, e.target.value)}
								title="Décimale (kg)"
							>
								{DEC_OPTIONS.map((d) => (
									<option key={d} value={d}>{formatDec(d)}</option>
								))}
							</select>
							<span className="editor-series-input-unit">kg</span>
						</div>

						{/* Colonne RPE vide — les warmups n'en ont pas */}
						<div className="col-rpe" />

						<div className="col-menu">
							<button
								type="button"
								className="editor-series-row-menu-btn"
								onClick={() => setOpenMenu(openMenu === warmupIndex ? null : warmupIndex)}
								title="Plus"
								aria-label="Options de l'échauffement"
							>
								<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
									<circle cx="5" cy="12" r="1.6" />
									<circle cx="12" cy="12" r="1.6" />
									<circle cx="19" cy="12" r="1.6" />
								</svg>
							</button>
							<RowMenu
								open={openMenu === warmupIndex}
								onClose={() => setOpenMenu(null)}
								onRemove={() => onRemoveWarmup?.(warmupIndex)}
								canRemove={true}
							/>
						</div>
					</div>
				);
			})}

			<button type="button" className="editor-warmup-add-row" onClick={onAddWarmup}>
				+ Ajouter un échauffement
			</button>
		</div>
	);
}
